"use client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { AppCard } from "./app-card";
import { useState, useMemo, useEffect } from "react";
import { App } from "@/lib/types/app";
import { AppCardComingSoon } from "./app-card-coming-soon";
import comingsoon from "@/lib/comingsoon/comingsoon.json";
import { useAppConfigs } from "@/hooks/use-app-config";
import { ArrowUp, ArrowDown } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

function normalize(str: string): string {
  return str.toLowerCase().replace(/[\s\-_]/g, "");
}

enum ConfigurationFilter {
  ALL = "all",
  CONFIGURED = "configured",
  UNCONFIGURED = "unconfigured",
}

enum AuthType {
  ALL = "All Auth Types",
  API_KEY = "api_key",
  XML_RPC = "xml_rpc",
  OAUTH2 = "oauth2",
  NO_AUTH = "no_auth",
}

enum SortOption {
  ASC = "asc",
  DESC = "desc",
}

interface AppGridProps {
  apps: App[];
  totalApps?: number;
  allCategories?: string[];
  allAuthTypes?: string[];
  onFiltersChange?: (filters: {
    searchQuery?: string;
    category?: string;
    authType?: string;
    sortOrder?: string;
    configurationFilter?: string;
  }) => void;
  onPageChange?: (page: number) => void;
  currentPage?: number;
  pageSize?: number;
  totalPages?: number;
}

export function AppGrid({ 
  apps,
  totalApps,
  allCategories = [],
  allAuthTypes = [],
  onFiltersChange,
  onPageChange,
  currentPage = 0,
  pageSize = 50,
  totalPages = 1,
}: AppGridProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] =
    useState<string>("All Categories");
  const [selectedAuthType, setSelectedAuthType] = useState<string>(
    AuthType.ALL,
  );
  const [sortOrder, setSortOrder] = useState<string>(SortOption.ASC);
  const [configurationFilter, setConfigurationFilter] = useState<string>(
    ConfigurationFilter.ALL,
  );

  const { data: appConfigs = [] } = useAppConfigs();
  
  // Notify parent of filter changes
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange({
        searchQuery: searchQuery || undefined,
        category: selectedCategory !== "All Categories" ? selectedCategory : undefined,
        authType: selectedAuthType !== AuthType.ALL ? selectedAuthType : undefined,
        sortOrder,
        configurationFilter,
      });
    }
  }, [searchQuery, selectedCategory, selectedAuthType, sortOrder, configurationFilter, onFiltersChange]);

  // Use provided categories and auth types, or extract from current apps
  const categories = allCategories.length > 0 
    ? allCategories 
    : Array.from(new Set(apps.flatMap((app) => app.categories)));

  const authTypes = allAuthTypes.length > 0
    ? allAuthTypes.filter((authType) =>
        [AuthType.API_KEY, AuthType.XML_RPC, AuthType.OAUTH2, AuthType.NO_AUTH].includes(
          authType as AuthType,
        ),
      )
    : Array.from(
        new Set(
          apps.flatMap((app) => Object.keys(app.supported_security_schemes || {})),
        ),
      ).filter((authType) =>
        [AuthType.API_KEY, AuthType.XML_RPC, AuthType.OAUTH2, AuthType.NO_AUTH].includes(
          authType as AuthType,
        ),
      );

  const configuredAppNames = useMemo(() => {
    return new Set(appConfigs.map((config) => config.app_name));
  }, [appConfigs]);

  // Client-side filter for configuration status only (not in backend)
  const filteredApps = useMemo(() => {
    if (configurationFilter === ConfigurationFilter.ALL) {
      return apps;
    }
    
    return apps.filter((app) => {
      const isConfigured = configuredAppNames.has(app.name);
      
      if (configurationFilter === ConfigurationFilter.CONFIGURED) {
        return isConfigured;
      } else if (configurationFilter === ConfigurationFilter.UNCONFIGURED) {
        return !isConfigured;
      }
      
      return true;
    });
  }, [apps, configurationFilter, configuredAppNames]);

  const liveAppKeys = useMemo(() => {
    const keys = new Set<string>();
    apps.forEach((app) => {
      keys.add(normalize(app.name));
      // Since the names are not uniform, use a filter to make unique
      if (app.display_name && app.display_name !== app.name) {
        keys.add(normalize(app.display_name));
      }
    });
    return keys;
  }, [apps]);

  const comingSoonApps = useMemo(() => {
    return comingsoon.filter((app) => !liveAppKeys.has(normalize(app.title)));
  }, [liveAppKeys]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search apps by name, description, or category..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-sm"
        />

        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-[170px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All Categories">All Categories</SelectItem>
            {categories.map((category) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedAuthType} onValueChange={setSelectedAuthType}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={AuthType.ALL}>All Auth Types</SelectItem>
            {authTypes.map((authType) => (
              <SelectItem key={authType} value={authType}>
                {authType === AuthType.API_KEY && "API Key"}
                {authType === AuthType.OAUTH2 && "OAuth2"}
                {authType === AuthType.NO_AUTH && "No Auth"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <ToggleGroup
          type="single"
          value={configurationFilter}
          onValueChange={(value) =>
            setConfigurationFilter(value || ConfigurationFilter.ALL)
          }
          className="border rounded-md"
        >
          <ToggleGroupItem
            value={ConfigurationFilter.ALL}
            aria-label="Show all apps"
            className="flex-none px-4"
          >
            All
          </ToggleGroupItem>
          <ToggleGroupItem
            value={ConfigurationFilter.CONFIGURED}
            aria-label="Show configured apps"
            className="flex-none px-4"
          >
            Configured
          </ToggleGroupItem>
          <ToggleGroupItem
            value={ConfigurationFilter.UNCONFIGURED}
            aria-label="Show unconfigured apps"
            className="flex-none px-4"
          >
            Unconfigured
          </ToggleGroupItem>
        </ToggleGroup>

        <ToggleGroup
          type="single"
          value={sortOrder}
          onValueChange={(value) => setSortOrder(value || SortOption.ASC)}
          className="border rounded-md"
        >
          <ToggleGroupItem
            value={SortOption.ASC}
            aria-label="Sort A to Z"
            className="flex-none px-3"
          >
            <ArrowUp className="h-4 w-4" />
            A-Z
          </ToggleGroupItem>
          <ToggleGroupItem
            value={SortOption.DESC}
            aria-label="Sort Z to A"
            className="flex-none px-3"
          >
            <ArrowDown className="h-4 w-4" />
            Z-A
          </ToggleGroupItem>
        </ToggleGroup>

        {/* <Select onValueChange={setSelectedTag}>
          <SelectTrigger className="w-[80px]">
            <SelectValue placeholder="Tags" />
          </SelectTrigger>
          <SelectContent>
            {["all", ...tags].map((tag) => (
              <SelectItem key={tag} value={tag}>
                {tag}
              </SelectItem>
            ))}
          </SelectContent>
        </Select> */}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filteredApps.map((app) => (
          <AppCard
            key={app.id}
            app={app}
            isConfigured={configuredAppNames.has(app.name)}
          />
        ))}
      </div>

      {filteredApps.length === 0 && (
        <div className="text-center text-muted-foreground">
          No apps found matching your criteria
        </div>
      )}
      
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-muted-foreground">
            Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalApps || 0)} of {totalApps || 0} apps
          </div>
          
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => onPageChange && currentPage > 0 && onPageChange(currentPage - 1)}
                  className={currentPage === 0 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
              
              {/* Show first page */}
              {currentPage > 2 && (
                <>
                  <PaginationItem>
                    <PaginationLink 
                      onClick={() => onPageChange && onPageChange(0)}
                      className="cursor-pointer"
                    >
                      1
                    </PaginationLink>
                  </PaginationItem>
                  {currentPage > 3 && (
                    <PaginationItem>
                      <span className="px-2">...</span>
                    </PaginationItem>
                  )}
                </>
              )}
              
              {/* Show pages around current page */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(0, Math.min(currentPage - 2, totalPages - 5)) + i;
                if (pageNum >= totalPages) return null;
                
                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      onClick={() => onPageChange && onPageChange(pageNum)}
                      isActive={pageNum === currentPage}
                      className="cursor-pointer"
                    >
                      {pageNum + 1}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              
              {/* Show last page */}
              {currentPage < totalPages - 3 && (
                <>
                  {currentPage < totalPages - 4 && (
                    <PaginationItem>
                      <span className="px-2">...</span>
                    </PaginationItem>
                  )}
                  <PaginationItem>
                    <PaginationLink 
                      onClick={() => onPageChange && onPageChange(totalPages - 1)}
                      className="cursor-pointer"
                    >
                      {totalPages}
                    </PaginationLink>
                  </PaginationItem>
                </>
              )}
              
              <PaginationItem>
                <PaginationNext 
                  onClick={() => onPageChange && currentPage < totalPages - 1 && onPageChange(currentPage + 1)}
                  className={currentPage >= totalPages - 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
